import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Clock,
  XCircle,
  CheckCircle2,
  LineChart,
  ArrowDownLeft,
  ArrowUpRight,
  Repeat,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";

function KycBanner({ status, reason }) {
  const map = {
    approved: {
      c: "bg-emerald-50 text-emerald-700 border-emerald-200",
      Icon: CheckCircle2,
      label: "KYC Approved — you can start trading.",
    },
    pending_review: {
      c: "bg-amber-50 text-amber-700 border-amber-200",
      Icon: Clock,
      label: "KYC under review — usually takes 24–48 hours.",
    },
    rejected: {
      c: "bg-red-50 text-red-700 border-red-200",
      Icon: XCircle,
      label: `KYC rejected: ${reason || "please re-upload your documents"}`,
    },
    personal_done: {
      c: "bg-blue-50 text-cm-blue border-blue-200",
      Icon: ShieldCheck,
      label: "Your KYC is incomplete — upload your documents.",
    },
    not_started: {
      c: "bg-blue-50 text-cm-blue border-blue-200",
      Icon: ShieldCheck,
      label: "Complete KYC to unlock trading.",
    },
  };
  const conf = map[status] || map.not_started;
  const { Icon, c, label } = conf;
  return (
    <div
      className={`flex items-start gap-3 rounded-[14px] border px-5 py-4 ${c}`}
      data-testid="kyc-banner"
    >
      <Icon size={18} className="mt-0.5" />
      <div className="flex-1 text-[14px] font-medium">{label}</div>
      {status === "rejected" && (
        <Link to="/kyc" className="text-[13px] font-semibold underline hover:no-underline">
          Re-upload now
        </Link>
      )}
      {(status === "not_started" || status === "personal_done") && (
        <Link
          to="/register"
          className="text-[13px] font-semibold underline hover:no-underline"
        >
          Complete now
        </Link>
      )}
    </div>
  );
}

function SuspendedBanner({ suspended, reason }) {
  if (!suspended) return null;
  return (
    <div
      className="flex items-start gap-3 rounded-[14px] border border-red-200 bg-red-50 text-red-700 px-5 py-4"
      data-testid="suspended-banner"
    >
      <XCircle size={18} className="mt-0.5" />
      <div className="flex-1 text-[14px] font-medium">
        <strong>Trading Suspended:</strong> {reason || "Your trading access has been temporarily suspended due to risk or compliance rules."}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, testid }) {
  return (
    <div
      className="bg-white rounded-[16px] border border-[var(--cm-border)] p-6"
      data-testid={testid}
    >
      <div className="text-[12px] text-cm-grey uppercase tracking-wider">{label}</div>
      <div className="text-[24px] font-bold text-cm-navy mt-2">{value}</div>
      {sub && <div className="text-[12px] text-cm-grey mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [marginHealth, setMarginHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/api/user/overview").then((r) => setData(r.data)),
      api.get("/api/safety/margin-health").then((r) => setMarginHealth(r.data))
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);


  return (
    <AppShell>
      {loading && !data ? (
        <div className="text-center text-cm-grey py-20">Loading…</div>
      ) : !data ? (
        <div className="text-center text-cm-grey py-20">Could not load account data.</div>
      ) : (
        <div className="space-y-6">
          <div>
            <h1 className="text-[26px] font-bold text-cm-navy tracking-tight">
              Welcome{data.user.full_name ? `, ${data.user.full_name}` : ""}
            </h1>
            <p className="mt-1 text-cm-grey text-[14px]">Here's your account snapshot.</p>
          </div>

          <KycBanner status={data.user.kyc_status} reason={data.user.kyc_rejection_reason} />
          <SuspendedBanner suspended={data.user.trading_suspended} reason={data.user.suspended_reason} />

          <div className="grid md:grid-cols-4 gap-5">
            <StatCard
              label="Total balance"
              value={`₹ ${(data.total_value_inr || 0).toLocaleString()}`}
              sub="Across all wallets"
              testid="dash-total-balance"
            />
            <StatCard
              label="24h P&L"
              value={`₹ ${(data.pnl_24h || 0).toLocaleString()}`}
              sub="Today's change"
              testid="dash-pnl"
            />
            <StatCard
              label="Assets"
              value={(data.assets || []).length}
              sub="Crypto balances"
              testid="dash-assets"
            />
            <StatCard
              label="Portfolio Health"
              value={marginHealth ? `${marginHealth.margin_health_score}%` : "100%"}
              sub={marginHealth ? `Status: ${marginHealth.status}` : "Healthy"}
              testid="dash-health-score"
            />
          </div>


          {(data.assets || []).length > 0 ? (
            <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-6" data-testid="dash-portfolio">
              <h3 className="text-[16px] font-bold text-cm-navy">Portfolio</h3>
              <p className="text-[12px] text-cm-grey mt-1">Live balances valued with Binance prices</p>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-left text-cm-grey border-b border-[var(--cm-border)]">
                      <th className="pb-2 font-semibold">Asset</th>
                      <th className="pb-2 font-semibold text-right">Balance</th>
                      <th className="pb-2 font-semibold text-right">USD</th>
                      <th className="pb-2 font-semibold text-right">INR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.assets.map((a) => (
                      <tr key={a.symbol} className="border-b border-[var(--cm-border)] last:border-0">
                        <td className="py-3 font-semibold text-cm-navy">{a.symbol}</td>
                        <td className="py-3 text-right tabular-nums">{Number(a.balance).toLocaleString()}</td>
                        <td className="py-3 text-right tabular-nums text-cm-grey">
                          ${(Number(a.balance) * Number(a.price_usd || 0)).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </td>
                        <td className="py-3 text-right tabular-nums">
                          ₹{(Number(a.value_inr || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-6">
            <h3 className="text-[16px] font-bold text-cm-navy">Quick actions</h3>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { Icon: ArrowDownLeft, label: "Deposit", to: "/wallets", disabled: !data.can_trade },
                { Icon: ArrowUpRight, label: "Withdraw", to: "/wallets", disabled: !data.can_trade },
                { Icon: Repeat, label: "Trade", to: "/trade", disabled: !data.can_trade },
                { Icon: LineChart, label: "Markets", to: "/markets" },
              ].map(({ Icon, label, to, disabled }) => (
                <Link
                  key={label}
                  to={disabled ? "#" : to}
                  onClick={(e) => disabled && e.preventDefault()}
                  className={`flex flex-col items-start gap-2 p-4 rounded-[12px] border border-[var(--cm-border)] transition ${
                    disabled
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:border-cm-blue hover:bg-cm-lavender"
                  }`}
                  data-testid={`quick-${label.toLowerCase()}`}
                >
                  <Icon size={18} className="text-cm-blue" />
                  <span className="text-[13px] font-semibold text-cm-navy">{label}</span>
                  {disabled && (
                    <span className="text-[11px] text-cm-grey">Needs KYC approval</span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-6">
            <h3 className="text-[16px] font-bold text-cm-navy">Account security</h3>
            <ul className="mt-4 space-y-3 text-[13px]">
              {[
                { ok: data.user.email_verified, label: "Email verified" },
                { ok: data.user.mobile_verified, label: "Mobile verified" },
                { ok: data.user.totp_enabled, label: "Two-factor authentication (2FA) enabled" },
                { ok: data.user.kyc_status === "approved", label: "KYC approved" },
              ].map((r) => (
                <li key={r.label} className="flex items-center gap-2.5">
                  <span
                    className={`w-5 h-5 rounded-full grid place-items-center text-[11px] ${
                      r.ok ? "bg-emerald-500 text-white" : "bg-[#EEF1F7] text-cm-grey"
                    }`}
                  >
                    {r.ok ? "✓" : "•"}
                  </span>
                  <span className="text-cm-navy">{r.label}</span>
                  <span
                    className={`ml-auto text-[12px] ${
                      r.ok ? "text-emerald-600" : "text-cm-grey"
                    }`}
                  >
                    {r.ok ? "Done" : "Pending"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </AppShell>
  );
}
